// src/pages/Insights/components/Charts/mini/tooltipFormatter.ts

import { chartConfig } from "../chartConfig";
import { escapeHtml } from "src/pages/Insights/transformer";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { resolveStatMarkerRows, buildStatMarkerTooltip, IResolvedCIRow, resolveMarkerSymbol } from "../components/StatMarkerUtils";
import { formatNumber } from "src/pages/Insights/transformer/dashboardUtils";
import { getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_COLOR } from "./chartHelpers";
import { useEffect, useRef, useCallback } from "react";

// Safe: static literal pattern validating a hex or rgb() color string; bounded
// quantifiers with no backtracking risk — not a user-controlled ReDoS sink.
const SAFE_COLOR_REGEX = /^(#[0-9A-Fa-f]{6}|rgb\((?:[01]?\d?\d|2[0-4]\d|25[0-5]),(?:[01]?\d?\d|2[0-4]\d|25[0-5]),(?:[01]?\d?\d|2[0-4]\d|25[0-5])\))$/;

// ─────────────────────────────────────────────
// Tooltip Formatter
// ─────────────────────────────────────────────

/**
 * Series item shape expected by the tooltip helper.
 * All mini chart components pass their series array which matches this.
 */
interface ITooltipSeriesItem {
	name: string;
	value: number | string;
	n_size: number | string;
	stat_marker?: IStatMarker | null;
}

/**
 * Shared tooltip formatter for all chart types.
 * Handles both separate and inline stat marker tooltip modes.
 *
 * @param params - ECharts tooltip callback params
 * @param series - The original series array from the component
 * @param labelFormat - The label format string (e.g., "##.#%")
 * @param statTestConfig - Stat test configuration (nullable)
 * @param colorMap - Record<seriesName, color> for separate tooltip mode
 * @returns Formatted HTML string for the tooltip
 */
export const buildChartTooltip = (
	params: any,
	series: ITooltipSeriesItem[],
	labelFormat: string,
	statTestConfig?: IStatTestConfig | null,
	colorMap?: Record<string, string>,
): string => {
	const useSeparateMarkerTooltip = statTestConfig?.separate_marker_tooltip ?? false;
	const seriesName = params.seriesName || params.data?.name || "";

	// --- Separate tooltip mode: markPoint hover shows stat-only tooltip ---
	if (useSeparateMarkerTooltip && params.componentType === "markPoint" && statTestConfig) {
		const seriesColor = colorMap?.[seriesName] || "#333";
		const matchedSeries = series.find((s) => s.name === seriesName);
		const resolvedRows = matchedSeries ? resolveStatMarkerRows(matchedSeries.stat_marker, statTestConfig) : [];

		return buildStatMarkerTooltip(seriesName, seriesColor, resolvedRows, statTestConfig.show_p_value);
	}

	// --- Inline mode: resolve stat markers for the series ---
	let resolvedRows: IResolvedCIRow[] = [];
	if (statTestConfig?.enabled) {
		// For markPoint hovers, stat_marker comes from dataPointMeta (set per-point).
		// For line-point hovers, stat_marker is on params.data directly.
		// Never fall back to series.find() — it collides on duplicate product names
		// across waves (dict-format data), always returning wave 1's marker.
		const directMarker = params.data?.dataPointMeta?.stat_marker ?? params.data?.stat_marker;
		if (directMarker) {
			resolvedRows = resolveStatMarkerRows(directMarker, statTestConfig);
		}
	}

	// When hovering markPoint, params.data is the markPoint object, not series data.
	// Patch it using dataPointMeta — the authoritative per-point source.
	let effectiveParams = params;
	if (params.componentType === "markPoint") {
		const meta = params.data?.dataPointMeta;
		// Only use meta. series.find() falls back to the wrong wave when product
		// names repeat across the x-axis (dict-format multi-wave data).
		if (meta) {
			const val = meta.value;
			const nSize = meta.n_size;

			const isNoDataValue =
				val === undefined || val === null || (typeof val === "string" && (val as string).toLowerCase() === "no data") || (typeof val === "number" && !Number.isFinite(val));

			const nSizeStatusLabel = getNSizeStatusLabel(nSize);
			const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;
			const suppressValue = nSizeStatusLabel !== null;

			const formattedVal = isNoDataValue || suppressValue ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formatNumber(val as number, labelFormat);

			effectiveParams = {
				...params,
				data: {
					...params.data,
					value: isNoDataValue || suppressValue ? 0 : val,
					n_size: isNoDataNSize ? NSizeLabelText.NoData : nSize,
					displayValue: formattedVal,
					name: meta?.name || seriesName,
					stat_marker: meta?.stat_marker ?? params.data?.stat_marker,
				},
			};
		}
	}

	return formatStackedTooltip(effectiveParams, labelFormat, resolvedRows.length > 0 ? resolvedRows : undefined, statTestConfig?.show_p_value);
};

/**
 * Common tooltip configuration for all charts.
 * - triggerOn: "none" — tooltip show/hide is controlled manually via useTooltipCoordination
 * - confine: true — prevents tooltip from overflowing the browser viewport
 * - appendToBody: true — prevents clipping by mini chart overflow:hidden containers
 */
export const getCommonTooltipConfig = (position?: "right" | "left") => {
	const tooltipConfig = chartConfig.getTooltipConfig();

	return {
		trigger: "item" as const,
		triggerOn: "none" as const, // We control show/hide manually
		confine: true, // Prevents tooltip from overflowing viewport
		appendToBody: true,
		backgroundColor: tooltipConfig.backgroundColor,
		borderColor: tooltipConfig.borderColor,
		borderWidth: tooltipConfig.borderWidth,
		textStyle: {
			color: tooltipConfig.textColor,
			fontSize: tooltipConfig.fontSize,
		},
		extraCssText: `z-index: ${tooltipConfig.zIndex}; box-shadow: ${tooltipConfig.boxShadow}; border-radius: ${tooltipConfig.borderRadius}; word-wrap: break-word; overflow-wrap: break-word;`,
		displayTransition: tooltipConfig.displayTransition,
		transitionDuration: tooltipConfig.transitionDuration,
	};
};

/**
 * Stacked tooltip formatter - shows series name, value, and optionally stat markers inline
 */
export const formatStackedTooltip = (
	params: any,
	labelFormat: string,
	resolvedRows?: {
		ciLevel: string;
		displayText: string;
		items: { marker_label: string; p_value: number | null; direction: string }[];
		style: { font_color: string; font_weight: number };
	}[],
	showPValue?: boolean,
): string => {
	const tooltipConfig = chartConfig.getTooltipConfig();
	const name = params.data?.name ?? params.seriesName;
	const n_size = params.data?.n_size;

	// Use displayValue if available, otherwise fall back to value
	const displayValue = params.data?.displayValue;
	const rawValue = params.data?.value ?? params.value;

	// Check if this is "No Data"
	const nSizeStatusLabel = getNSizeStatusLabel(n_size);
	const isNoDataValue =
		displayValue === undefined ||
		displayValue === null ||
		(typeof displayValue === "string" && (displayValue.toLowerCase() === "no data" || displayValue === NSizeLabelText.LowN));
	const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;
	const suppressValue = nSizeStatusLabel !== null;

	// Format display values
	const formattedValue = isNoDataValue || suppressValue
		? (nSizeStatusLabel ?? NSizeLabelText.NoData)
		: (displayValue ?? (labelFormat.endsWith("%") ? `${rawValue}%` : rawValue));

	const displayNSize = isNoDataNSize ? NSizeLabelText.NoData : n_size;

	// Determine n_size color - show warning color for "No Data" or low n
	const nSizeColor = isNoDataNSize || (typeof n_size === "number" && n_size < tooltipConfig.lowNSizeThreshold) ? tooltipConfig.lowNSizeColor : tooltipConfig.textColor;

	// Sanitize all user-provided data to prevent XSS
	const safeName = escapeHtml(name);
	const safeFormattedValue = escapeHtml(String(formattedValue));
	const safeNSize = escapeHtml(String(displayNSize));
	const safeColor = SAFE_COLOR_REGEX.test(params.color) ? params.color : "#333";

	// Build stat marker section if provided
	let statMarkerHtml = "";
	if (resolvedRows && resolvedRows.length > 0) {
		const rowsHtml = resolvedRows
			.map((row) => {
				const itemsHtml = row.items
					.map((item) => {
						const symbol = escapeHtml(resolveMarkerSymbol(item.marker_label));
						const pValueStr = showPValue && item.p_value != null ? ` <span style="color:#9CA3AF;">(p=${escapeHtml(item.p_value.toFixed(4))})</span>` : "";
						const directionIcon = item.direction === "higher" ? "↑" : item.direction === "lower" ? "↓" : "";
						return `<span style="color:${escapeHtml(row.style.font_color)}; font-weight:${row.style.font_weight};">${symbol}</span>${directionIcon}${pValueStr}`;
					})
					.join(",");

				return `
					<div style="margin-top: 2px;">
						<span style="font-size: 9px; color: #9CA3AF;">${escapeHtml(row.ciLevel)}% CI:</span>
						<span style="font-size: 10px;">${itemsHtml}</span>
					</div>`;
			})
			.join("");

		statMarkerHtml = `
			<div style="padding-left:16px; margin-top: 4px; border-top: 1px solid #E5E7EB; padding-top: 4px;">
				<div style="font-size: 9px; color: #6B7280; font-weight: 600; margin-bottom: 2px;">Stat Markers</div>
				${rowsHtml}
			</div>`;
	}

	return `
    <div style="min-width:100px; max-width: 22rem; white-space:normal;">
      <div style="display:flex; align-items:center; gap:8px; margin-bottom:2px;">
        <span style="
          display:inline-block;
          width:10px;
          height:10px;
          border-radius:50%;
          background:${safeColor};
          flex-shrink:0;
        "></span>
        <span style="
          font-weight:600;
          word-wrap:break-word;
          overflow-wrap:break-word;
          white-space:normal;
          flex:1;
          min-width:0;
        ">${safeName}</span>
      </div>
      <div style="padding-left:16px; white-space:normal;${isNoDataValue || suppressValue ? ` color:${SUPPRESSED_VALUE_COLOR};` : ""}">
        Value= ${safeFormattedValue}
      </div>
      <div style="padding-left:16px; color:${nSizeColor}; white-space:normal;">
        n= ${safeNSize}
      </div>
      ${statMarkerHtml}
    </div>
  `;
};

// ─────────────────────────────────────────────
// Tooltip Coordinator (global registry)
// ─────────────────────────────────────────────

export interface IRegisteredChart {
	dismiss: () => void;
	isAlive: () => boolean;
}

const registry = new Set<IRegisteredChart>();

export const tooltipCoordinator = {
	register(entry: IRegisteredChart): () => void {
		registry.add(entry);
		return () => {
			registry.delete(entry);
		};
	},

	/** Dismiss every other chart's tooltip. Auto-prunes dead entries. */
	dismissOthers(except: IRegisteredChart): void {
		registry.forEach((entry) => {
			if (entry === except) return;
			if (!entry.isAlive()) {
				registry.delete(entry);
				return;
			}
			entry.dismiss();
		});
	},
};

// ─────────────────────────────────────────────
// useTooltipCoordination Hook
// ─────────────────────────────────────────────

const TOOLTIP_SHOW_DELAY = 100;

export const useTooltipCoordination = () => {
	const chartRef = useRef<any>(null);
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	const clearShowTimer = useCallback(() => {
		if (showTimerRef.current !== null) {
			clearTimeout(showTimerRef.current);
			showTimerRef.current = null;
		}
	}, []);

	const dismiss = useCallback(() => {
		clearShowTimer();
		if (!mountedRef.current) return;
		const instance = chartRef.current?.getEchartsInstance?.();
		if (instance) {
			instance.dispatchAction({ type: "hideTip" });
		}
	}, [clearShowTimer]);

	const isAlive = useCallback(() => mountedRef.current, []);

	// Stable registry entry — always points to latest dismiss/isAlive
	// without causing re-registration on renders
	const registryEntryRef = useRef({ dismiss, isAlive });
	registryEntryRef.current.dismiss = dismiss;
	registryEntryRef.current.isAlive = isAlive;

	useEffect(() => {
		mountedRef.current = true;
		const unregister = tooltipCoordinator.register(registryEntryRef.current);
		return () => {
			mountedRef.current = false;
			clearShowTimer();
			unregister();
		};
	}, [clearShowTimer]);

	const onMouseOver = useCallback(
		(params: any) => {
			clearShowTimer();
			tooltipCoordinator.dismissOthers(registryEntryRef.current);

			const instance = chartRef.current?.getEchartsInstance?.();
			if (!instance) return;

			showTimerRef.current = setTimeout(() => {
				showTimerRef.current = null;
				if (!mountedRef.current) return;

				// For markPoint hovers, target the underlying series data point so the
				// tooltip shows the bar/line value + stat markers. Using params.name
				// fails for type:"max" markPoints (no resolvable name); seriesIndex +
				// dataIndex reliably resolves to the data point the markPoint sits on.
				instance.dispatchAction({
					type: "showTip",
					seriesIndex: params.seriesIndex,
					dataIndex: params.dataIndex ?? 0,
				});
			}, TOOLTIP_SHOW_DELAY);
		},
		[clearShowTimer],
	);

	const onMouseOut = useCallback(() => {
		clearShowTimer();
		if (!mountedRef.current) return;
		const instance = chartRef.current?.getEchartsInstance?.();
		if (instance) {
			instance.dispatchAction({ type: "hideTip" });
		}
	}, [clearShowTimer]);

	const onGlobalOut = useCallback(() => {
		clearShowTimer();
		if (!mountedRef.current) return;
		const instance = chartRef.current?.getEchartsInstance?.();
		if (instance) {
			instance.dispatchAction({ type: "hideTip" });
		}
	}, [clearShowTimer]);

	return {
		chartRef,
		onEvents: {
			mouseover: onMouseOver,
			mouseout: onMouseOut,
			globalout: onGlobalOut,
		},
	};
};
